import { z } from 'zod/v4'
import { KanbanConfigSchema } from './types.kanban'
import { SpreadsheetConfigSchema } from './types.spreadsheet'
import { FormConfigSchema } from './types.form'
import { LinkConfigSchema } from './types.link'
import { CalendarConfigSchema } from './types.calendar'
import { QuerySchema } from '../../queries/resources/types'
import { ActivitySchema } from '../../../../activity/resources'
import { ActionSchema } from '../../../../../../workflows/features/triggers/features/actions/resources/types'
// NOTE: Inline to avoid circular import with pages/resources/types
const CommunicationChannelSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  // Channel handle from executable.config (skedyul.config.ts)
  handle: z.string(),
  identifierValue: z.string(),
  appInstallationId: z.string(),
  // Resolved from executable config capabilities.messaging.send_batch
  sendBatch: z.string().optional(),
  // Contact association links for this channel
  contactAssociationLinks: z
    .array(
      z.object({
        id: z.string(),
        modelId: z.string(),
      }),
    )
    .optional(),
})

export const SortStructureSchema = z.object({
  field: z.string(),
  direction: z.enum(['asc', 'desc']),
})

const SectionOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
})

export const SectionSchema = z.object({
  field: z.string(),
  groupingKey: z.string(),
  color: z.string().optional(),
  // Optional explicit options for this section (label/value pairs)
  options: z.array(SectionOptionSchema).optional(),
})

export const SubsectionSchema = SectionSchema.omit({
  color: true,
})

export const ViewableFieldSchema = z.object({
  model: z.object({
    id: z.string(),
    handle: z.string(),
    name: z.string(),
  }),
  id: z.string(),
  handle: z.string(),
  label: z.string(),
  dataType: z.enum(['TEXT', 'NUMBER', 'DATE', 'BOOLEAN', 'RELATION']),
})

/**
 * Canonical block config container used across views.
 *
 * - `type` identifies the active view for a block.
 * - `config` holds per-view configs side‑by‑side so we can switch views
 *   without losing the previous view's configuration.
 * - Shared fields (`sort`, `fields`, `sections`, `subSections`) apply to
 *   all views and should be preserved when toggling between spreadsheet,
 *   kanban, calendar, etc.
 */
export const BlockConfigSchema = z.object({
  type: z.enum(['kanban', 'spreadsheet', 'form', 'link', 'calendar']),
  config: z.object({
    kanban: KanbanConfigSchema.optional(),
    spreadsheet: SpreadsheetConfigSchema.optional(),
    form: FormConfigSchema.optional(),
    link: LinkConfigSchema.optional(),
    calendar: CalendarConfigSchema.optional(),
  }),
  sort: SortStructureSchema.optional(),
  fields: z.array(ViewableFieldSchema),

  // section and subsection are optional because they are shared across all views
  section: SectionSchema.optional(),
  subsection: SubsectionSchema.optional(),
})

export const CreateBlockSchema = z.object({
  title: z.string(),
  query: QuerySchema.optional(),
  config: BlockConfigSchema,
  modelId: z.string(),
  default: z.boolean().optional(),
})

export const BlockSchema = z.object({
  id: z.string(),
  title: z.string(),
  query: z.any(), // QuerySchema
  config: z.any(), // BlockConfigSchema
  default: z.boolean(),
  modelId: z.string(),
  data: z.unknown(),
  generatedLabel: z.string(),
  generatedImageSeed: z.string().nullable(),
  activity: z.array(ActivitySchema),
  actions: z.array(ActionSchema),
  communicationChannels: z.array(CommunicationChannelSummarySchema),
  contact: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  pagination: z
    .object({
      page: z.number(),
      total: z.number(),
      hasMore: z.boolean(),
      limit: z.number(),
    })
    .optional(),
  relationshipLink: z
    .object({
      id: z.string(),
      relationshipId: z.string(),
      sourceModelId: z.string(),
      targetModelId: z.string(),
      cardinality: z.enum(['ONE_TO_ONE', 'ONE_TO_MANY', 'MANY_TO_ONE']),
    })
    .optional()
    .nullable(),
})

export type GetBlock = z.infer<typeof BlockSchema>
export type CreateBlock = z.infer<typeof CreateBlockSchema>
export type BlockConfig = z.infer<typeof BlockConfigSchema>
